/**
 * Shared slugify — the single source of truth for heading-id generation.
 *
 * P1.2 from the 2026-05-23 codex audit: the editor scrollSync was building
 * its own line→heading-id map with a local slugifier, and markdown-it-anchor
 * was running a DIFFERENT slugifier with duplicate-suffix handling
 * (`#heading`, `#heading-1`, `#heading-2`). Result: docs with duplicate
 * headings sync wrong (always to first match). The fix is to export the
 * slug function from ONE place — both the anchor plugin and the scrollSync
 * map import the same function, including the same duplicate-counter
 * behavior.
 *
 * Mirrors markdown-it-anchor's default `uniqueSlug` semantics: second
 * occurrence becomes `slug-1`, third `slug-2`, etc. Caller maintains the
 * counter as a Map<string, number> tracked across the document; pass a
 * fresh map per render so counters don't bleed between docs.
 */

/**
 * Pure slugify — lowercases, strips non-word/space/hyphen, hyphenates
 * spaces. Pure function, no state.
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
}

/**
 * uniqueSlug — applies slugify() then disambiguates against the provided
 * counter map. First occurrence keeps the bare slug; subsequent occurrences
 * append `-1`, `-2`, etc. matches markdown-it-anchor's default behavior.
 *
 * @param text  raw heading text
 * @param seen  per-document map of slug→count (mutated)
 */
export function uniqueSlug(text: string, seen: Map<string, number>): string {
  const base = slugify(text)
  const count = seen.get(base) ?? 0
  seen.set(base, count + 1)
  if (count === 0) return base
  return `${base}-${count}`
}
